/**
 * AurelionRoutingPage — Admin Phase 1c · cross-org brief observer.
 *
 * Founder ruling Feb 2026:
 *   "Add an admin Concierge requests observer view to watch cross-org
 *    routing ticket IDs without opening Zoho."
 *
 * Reads /api/os-admin/aurelion-routing · strictly READ-ONLY.
 * Shows every brief our backend dispatched into the MindEscapes
 * network (tenant=aurelion|mindescapes) along with their Zoho ticket
 * ID returned by the remote receiver. Filters: tenant · benefit ·
 * status · search · window.
 */
import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";

const TENANTS  = ["aurelion", "mindescapes"];
const BENEFITS = ["table", "escape", "enjoy"];
const STATUSES = ["delivered", "pending", "failed", "pending_secret"];
const WINDOWS  = [
  { hrs: 24,        label: "24h" },
  { hrs: 168,       label: "7d"  },
  { hrs: 24 * 30,   label: "30d" },
  { hrs: 24 * 90,   label: "90d" },
];

function pillTone(status) {
  if (status === "delivered")      return { bg: "#e6f3e9", fg: "#1e6b32" };
  if (status === "pending")        return { bg: "#fdf4dc", fg: "#7a5a07" };
  if (status === "failed")         return { bg: "#fbe2e2", fg: "#922222" };
  if (status === "pending_secret") return { bg: "#ecebf8", fg: "#3b2e8a" };
  return { bg: "#eee", fg: "#444" };
}

function relTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return iso; }
}

export default function AurelionRoutingPage() {
  const [rows, setRows]         = useState([]);
  const [counters, setCounters] = useState({});
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [detail, setDetail]     = useState(null);
  const [filters, setFilters]   = useState({
    tenant:  "",
    benefit: "",
    status:  "",
    q:       "",
    sinceHrs: 168,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const p = new URLSearchParams();
        if (filters.tenant)  p.set("tenant",  filters.tenant);
        if (filters.benefit) p.set("benefit", filters.benefit);
        if (filters.status)  p.set("status",  filters.status);
        if ((filters.q || "").trim()) p.set("q", filters.q.trim());
        p.set("since_hours", String(filters.sinceHrs));
        p.set("limit", "200");
        const r = await api.get(`/os-admin/aurelion-routing?${p.toString()}`);
        if (cancelled) return;
        setRows(r.data.rows || []);
        setCounters(r.data.counters_window || {});
      } catch (e) {
        if (cancelled) return;
        setError(
          formatApiError(e?.response?.data?.detail) ||
          "We could not load cross-org submissions just now."
        );
        setRows([]);
        setCounters({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filters]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const total = useMemo(
    () => Object.values(counters).reduce((a, b) => a + (b || 0), 0),
    [counters]
  );

  return (
    <div className="os-fyi-queue" data-testid="os-aurelion-routing">
      <div className="os-pagehead">
        <h1>Aurelion Routing</h1>
        <span className="count">
          {loading ? "Loading…" : `${rows.length} shown · ${total} in window`}
        </span>
      </div>
      <div className="os-pagesub">
        Cross-org concierge briefs · forwarded to the MindEscapes-network Zoho
        via signed webhook · <code>embed_submissions</code> · read-only
      </div>

      {/* ── Status counters ───────────────────────────────────── */}
      <div className="os-toolbar" data-testid="os-aurelion-counters" style={{ marginTop: 8 }}>
        {STATUSES.map((s) => (
          <span
            key={s}
            className="os-fchip"
            style={{
              cursor: "default",
              background: pillTone(s).bg,
              color: pillTone(s).fg,
              border: "none",
              fontVariantNumeric: "tabular-nums",
            }}
            data-testid={`os-aurelion-counter-${s}`}
          >
            {s.replace("_", " ")} · {counters[s] || 0}
          </span>
        ))}
      </div>

      {/* ── Filter block ──────────────────────────────────────── */}
      <div className="os-fyi-filterblock" data-testid="os-aurelion-filters">
        <div className="os-toolbar">
          <span className="os-fyi-label">Tenant</span>
          <button
            type="button"
            className={`os-fchip ${filters.tenant === "" ? "on" : ""}`}
            onClick={() => setF("tenant", "")}
            data-testid="os-aurelion-filter-tenant-all"
          >all</button>
          {TENANTS.map((t) => (
            <button
              key={t}
              type="button"
              className={`os-fchip ${filters.tenant === t ? "on" : ""}`}
              onClick={() => setF("tenant", t)}
              data-testid={`os-aurelion-filter-tenant-${t}`}
            >{t}</button>
          ))}
        </div>

        <div className="os-toolbar">
          <span className="os-fyi-label">Surface</span>
          <button
            type="button"
            className={`os-fchip ${filters.benefit === "" ? "on" : ""}`}
            onClick={() => setF("benefit", "")}
            data-testid="os-aurelion-filter-benefit-all"
          >all</button>
          {BENEFITS.map((b) => (
            <button
              key={b}
              type="button"
              className={`os-fchip ${filters.benefit === b ? "on" : ""}`}
              onClick={() => setF("benefit", b)}
              data-testid={`os-aurelion-filter-benefit-${b}`}
            >{b}</button>
          ))}
        </div>

        <div className="os-toolbar">
          <span className="os-fyi-label">Status</span>
          <button
            type="button"
            className={`os-fchip ${filters.status === "" ? "on" : ""}`}
            onClick={() => setF("status", "")}
            data-testid="os-aurelion-filter-status-all"
          >all</button>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`os-fchip ${filters.status === s ? "on" : ""}`}
              onClick={() => setF("status", s)}
              data-testid={`os-aurelion-filter-status-${s}`}
            >{s.replace("_", " ")}</button>
          ))}

          <span className="os-fyi-label os-fyi-label-spaced">Window</span>
          {WINDOWS.map((w) => (
            <button
              key={w.hrs}
              type="button"
              className={`os-fchip ${filters.sinceHrs === w.hrs ? "on" : ""}`}
              onClick={() => setF("sinceHrs", w.hrs)}
              data-testid={`os-aurelion-filter-window-${w.label}`}
            >{w.label}</button>
          ))}

          <span className="os-spacer" />
          <input
            type="search"
            placeholder="Search lc_ref · email · card · upstream_ref"
            value={filters.q}
            onChange={(e) => setF("q", e.target.value)}
            maxLength={120}
            className="os-fyi-search"
            data-testid="os-aurelion-search-input"
          />
        </div>
      </div>

      {error && (
        <div className="os-error" data-testid="os-aurelion-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────── */}
      <div className="os-table-wrap" data-testid="os-aurelion-table-wrap" style={{ marginTop: 12 }}>
        <table className="os-table">
          <thead>
            <tr>
              <th>When</th>
              <th>LC Ref</th>
              <th>Tenant</th>
              <th>Surface</th>
              <th>Member</th>
              <th>Brief</th>
              <th>Status</th>
              <th>Zoho Ticket</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(!loading && rows.length === 0) && (
              <tr>
                <td colSpan={9} style={{ textAlign: "center", padding: 32, color: "#7a7268" }}>
                  No submissions in this window.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const tone = pillTone(r.delivery_status);
              return (
                <tr key={r.id} data-testid={`os-aurelion-row-${r.lc_ref}`}>
                  <td title={r.created_at}>{relTime(r.created_at)}</td>
                  <td><code>{r.lc_ref}</code></td>
                  <td>{r.tenant}</td>
                  <td>{r.benefit}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{r.member_name || "—"}</div>
                    <div style={{ fontSize: 11, color: "#7a7268" }}>{r.member_email || ""}</div>
                  </td>
                  <td style={{ maxWidth: 280 }}>
                    <div style={{ fontSize: 12.5, color: "#3a352c", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.request_summary || "—"}
                    </div>
                  </td>
                  <td>
                    <span
                      style={{
                        background: tone.bg, color: tone.fg,
                        padding: "3px 9px", borderRadius: 10,
                        fontSize: 11, fontWeight: 600,
                        textTransform: "uppercase",
                      }}
                      data-testid={`os-aurelion-status-${r.lc_ref}`}
                    >
                      {r.delivery_status || "—"}
                    </span>
                  </td>
                  <td>
                    {r.remote_ticket_id ? (
                      <code data-testid={`os-aurelion-zoho-${r.lc_ref}`}>
                        {r.remote_ticket_id}
                      </code>
                    ) : (
                      <span style={{ color: "#a59c8c" }}>—</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="os-fchip"
                      onClick={() => setDetail(r)}
                      data-testid={`os-aurelion-open-${r.lc_ref}`}
                    >View</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detail && (
        <AurelionDetailDrawer
          submissionId={detail.id}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function AurelionDetailDrawer({ submissionId, onClose }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/os-admin/aurelion-routing/${submissionId}`);
        if (cancelled) return;
        setData(r.data.submission || null);
      } catch (e) {
        if (cancelled) return;
        setError(
          formatApiError(e?.response?.data?.detail) ||
          "Could not load detail."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [submissionId]);

  return (
    <div
      role="dialog"
      data-testid="os-aurelion-detail"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(20,16,11,0.45)",
        display: "flex", justifyContent: "flex-end",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(640px, 92vw)",
          background: "#FCFAF4",
          color: "#211C15",
          height: "100%",
          padding: "22px 26px 30px",
          overflowY: "auto",
          boxShadow: "-10px 0 30px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Submission detail</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="os-aurelion-detail-close"
            style={{ background: "none", border: 0, fontSize: 22, cursor: "pointer", color: "#5C5447" }}
            aria-label="Close"
          >×</button>
        </div>
        {loading && <p style={{ marginTop: 18 }}>Loading…</p>}
        {error && <p style={{ marginTop: 18, color: "#922222" }} data-testid="os-aurelion-detail-error">{error}</p>}
        {data && (
          <div style={{ marginTop: 14, fontSize: 13 }}>
            <Field k="LC Ref"          v={<code>{data.lc_ref}</code>} />
            <Field k="Tenant"          v={data.tenant} />
            <Field k="Surface"         v={data.benefit} />
            <Field k="Status"          v={data.delivery_status} />
            <Field k="Zoho Ticket"     v={data.remote_ticket_id ? <code>{data.remote_ticket_id}</code> : "—"} />
            <Field k="Upstream Ref"    v={data.upstream_ref ? <code>{data.upstream_ref}</code> : "—"} />
            <Field k="Member"          v={data.member_name || "—"} />
            <Field k="Member Email"    v={data.member_email || "—"} />
            <Field k="Member Phone"    v={data.member_phone || "—"} />
            <Field k="Member Card"     v={data.member_card || "—"} />
            <Field k="Page URL"        v={data.page_url ? <a href={data.page_url} target="_blank" rel="noreferrer">{data.page_url}</a> : "—"} />
            <Field k="Session"         v={data.session_id || "—"} />
            <Field k="Created"         v={data.created_at} />
            <Field k="Delivered"       v={data.delivered_at || "—"} />
            <Field k="Attempts"        v={data.delivery_attempts_count} />

            <h3 style={{ marginTop: 22, fontSize: 14 }}>Brief</h3>
            <pre style={preStyle} data-testid="os-aurelion-detail-request">
              {JSON.stringify(data.request_full, null, 2)}
            </pre>

            <h3 style={{ marginTop: 18, fontSize: 14 }}>Delivery attempts</h3>
            <pre style={preStyle} data-testid="os-aurelion-detail-attempts">
              {JSON.stringify(data.delivery_attempts, null, 2)}
            </pre>

            {data.remote_response && Object.keys(data.remote_response).length > 0 && (
              <>
                <h3 style={{ marginTop: 18, fontSize: 14 }}>Remote response</h3>
                <pre style={preStyle} data-testid="os-aurelion-detail-remote">
                  {JSON.stringify(data.remote_response, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 12, padding: "5px 0", borderBottom: "1px solid #efe7d4" }}>
      <span style={{ color: "#8a8071", fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.5 }}>{k}</span>
      <span>{v}</span>
    </div>
  );
}

const preStyle = {
  background: "#F4EFE6",
  border: "1px solid #E5DCCB",
  borderRadius: 8,
  padding: 12,
  fontSize: 11.5,
  lineHeight: 1.45,
  color: "#2a241c",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 360,
  overflow: "auto",
};
